import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/common/BrandLogo';
import { MIN_PASSWORD_LENGTH, validatePassword } from '../../constants/password';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

function hasRecoveryParams() {
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const searchParams = new URLSearchParams(window.location.search);
  return (
    hashParams.get('type') === 'recovery'
    || searchParams.has('code')
    || searchParams.has('token_hash')
  );
}

function getUrlAuthError() {
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const searchParams = new URLSearchParams(window.location.search);
  return hashParams.get('error_description') || searchParams.get('error_description');
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [status, setStatus] = useState('checking');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    let recoveryReady = false;

    const urlError = getUrlAuthError();
    if (urlError) {
      setStatus('invalid');
      return undefined;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryReady = true;
        if (mounted) setStatus('ready');
      }
    });

    const verifyRecoverySession = async () => {
      await new Promise((resolve) => { setTimeout(resolve, 300); });

      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;

      if (recoveryReady || (session && hasRecoveryParams())) {
        setStatus('ready');
        return;
      }

      if (hasRecoveryParams()) {
        setStatus('invalid');
        return;
      }

      setStatus('invalid');
    };

    verifyRecoverySession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setSubmitting(true);
    try {
      await updatePassword(password);
      navigate('/app');
    } catch (err) {
      const message = err.message || '';
      if (message.toLowerCase().includes('expired') || message.toLowerCase().includes('invalid')) {
        setStatus('invalid');
      } else {
        setError(message || 'No se pudo actualizar la contraseña.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <BrandLogo size="lg" />
        </div>

        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-3xl p-8 shadow-sm">
          <h1 className="text-2xl font-black text-[var(--text-main)] uppercase italic tracking-tight mb-2 text-center">
            Nueva contraseña
          </h1>
          <p className="text-xs text-[var(--text-secondary)] text-center mb-8 uppercase tracking-widest">
            Elige una contraseña segura para tu cuenta
          </p>

          {status === 'checking' && (
            <p className="text-sm text-[var(--text-secondary)] text-center">Verificando enlace...</p>
          )}

          {status === 'invalid' && (
            <div className="space-y-6">
              <p className="text-sm text-rose-600 font-medium text-center leading-relaxed">
                Este enlace de recuperación ha expirado o no es válido. Solicita uno nuevo para
                restablecer tu contraseña.
              </p>
              <Link
                to="/recuperar-contrasena"
                className="block w-full text-center gfi-gradient-bg text-brand-navy py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02]"
              >
                Solicitar nuevo enlace
              </Link>
              <p className="text-center text-xs text-[var(--text-secondary)]">
                <Link to="/login" className="text-brand-cyan font-bold hover:underline">
                  Volver a iniciar sesión
                </Link>
              </p>
            </div>
          )}

          {status === 'ready' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">
                  Nueva contraseña
                </label>
                <input
                  type="password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">
                  Confirmar contraseña
                </label>
                <input
                  type="password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-cyan focus:ring-1 focus:ring-brand-cyan"
                />
              </div>

              {error && (
                <p className="text-sm text-rose-600 font-medium">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full gfi-gradient-bg text-brand-navy py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] disabled:opacity-60"
              >
                {submitting ? 'Guardando...' : 'Guardar contraseña'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
